import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Bell } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Alert {
  id: string;
  metric: string;
  operator: string;
  value: string;
}

const mockAlerts: Alert[] = [
  { id: '1', metric: 'Total NMV', operator: 'greater than', value: '20,000,000' },
  { id: '2', metric: 'Units Sold', operator: 'less than', value: '1,000' },
  { id: '3', metric: 'Stock Held', operator: 'less than', value: '5,000' },
  { id: '4', metric: 'Customer Churn Rate', operator: 'greater than', value: '10%' },
];

const operatorOptions = [
  { value: 'greater than', label: 'greater than' },
  { value: 'less than', label: 'less than' },
  { value: 'equals', label: 'equals' },
  { value: 'not equals', label: 'not equals' },
];

export default function AlertsManagement() {
  const { toast } = useToast();
  const [alerts, setAlerts] = useState<Alert[]>(mockAlerts);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null);
  const [formData, setFormData] = useState({
    metric: '',
    operator: 'greater than',
    value: '',
  });

  const handleOpenDialog = (alert?: Alert) => {
    if (alert) {
      setEditingAlert(alert);
      setFormData({
        metric: alert.metric,
        operator: alert.operator,
        value: alert.value,
      });
    } else {
      setEditingAlert(null);
      setFormData({
        metric: '',
        operator: 'greater than',
        value: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSaveAlert = () => {
    if (!formData.metric || !formData.value) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    if (editingAlert) {
      setAlerts(alerts.map(a => 
        a.id === editingAlert.id 
          ? { ...a, ...formData }
          : a
      ));
      toast({
        title: 'Success',
        description: 'Alert updated successfully',
      });
    } else {
      const newAlert: Alert = {
        id: Date.now().toString(),
        ...formData,
      };
      setAlerts([...alerts, newAlert]);
      toast({
        title: 'Success',
        description: 'Alert created successfully',
      });
    }

    setIsDialogOpen(false);
    setEditingAlert(null);
  };

  const handleDeleteAlert = (id: string) => {
    setAlerts(alerts.filter(a => a.id !== id));
    toast({
      title: 'Success',
      description: 'Alert deleted successfully',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Alert Rules</h2>
          <p className="text-muted-foreground mt-1">
            Configure alerts to monitor key metrics
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Alert
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
        {alerts.map((alert) => (
          <Card key={alert.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  <CardTitle className="text-base">Alert Rule</CardTitle>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(alert)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAlert(alert.id)}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">If</p>
                <p className="font-medium">
                  <span className="text-primary">{alert.metric}</span>{' '}
                  <span className="text-muted-foreground">{alert.operator}</span>{' '}
                  <span className="font-bold">{alert.value}</span>
                </p>
                <p className="text-sm text-muted-foreground">then alert me</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {alerts.length === 0 && (
        <Card className="py-12">
          <CardContent className="flex flex-col items-center justify-center text-center">
            <Bell className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No alerts configured</h3>
            <p className="text-muted-foreground mb-4">
              Create your first alert to start monitoring metrics
            </p>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Alert
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingAlert ? 'Edit Alert Rule' : 'Create Alert Rule'}
            </DialogTitle>
            <DialogDescription>
              Configure when you want to be alerted based on metric thresholds
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="metric">Metric</Label>
              <Input
                id="metric"
                placeholder="e.g., Total NMV, Units Sold"
                value={formData.metric}
                onChange={(e) => setFormData({ ...formData, metric: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="operator">Operator</Label>
              <Select
                value={formData.operator}
                onValueChange={(value) => setFormData({ ...formData, operator: value })}
              >
                <SelectTrigger id="operator">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {operatorOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="value">Threshold Value</Label>
              <Input
                id="value"
                placeholder="e.g., 20,000,000 or 10%"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              />
            </div>

            <div className="rounded-lg bg-muted p-4 space-y-1">
              <p className="text-sm font-medium">Preview:</p>
              <p className="text-sm">
                If <span className="font-medium text-primary">{formData.metric || '[metric]'}</span>{' '}
                <span className="text-muted-foreground">{formData.operator}</span>{' '}
                <span className="font-bold">{formData.value || '[value]'}</span> then alert me
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAlert}>
              {editingAlert ? 'Update Alert' : 'Create Alert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
